import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { t } from 'app/core/i18n/i18n';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import {
  Subject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  skip,
  switchMap,
} from 'rxjs';
import {
  BPGroupedApiItem,
  BPGroupedItem,
  BPGroupedResponse,
  BPGroupingCriteria,
} from '../../types/bp-grouping.types';
import { UnassignedPartnersPanelService } from '../unassigned-partners-panel.service';

@Injectable()
export class UnassignedPartnersGroupedService {
  private businessPartnerService = inject(BusinessPartnerService);
  private panelService = inject(UnassignedPartnersPanelService);
  private handlerMessage = inject(HandlerMessageService);

  // === STATE SIGNALS ===
  private _data = signal<BPGroupedItem[]>([]);
  private _totalItems = signal<number>(0);
  private _loading = signal<boolean>(false);
  private _loadingMore = signal<boolean>(false);
  private _hasMore = signal<boolean>(true);
  private _currentPage = signal<number>(1);

  readonly pageSize = 20;

  // === SHARED FROM PANEL ===
  readonly searchTerm = this.panelService.searchTerm;
  readonly groupingCriteria = this.panelService.groupingCriteria;
  readonly subRouteId = this.panelService.subRouteIdForGroupView;

  // === ACTION SUBJECTS ===
  readonly loadMore$ = new Subject<void>();
  readonly reload$ = new Subject<void>();

  // === PUBLIC COMPUTED ===
  readonly data = computed(() => this._data());
  readonly totalItems = computed(() => this._totalItems());
  readonly loading = computed(() => this._loading());
  readonly loadingMore = computed(() => this._loadingMore());
  readonly hasMore = computed(() => this._hasMore());

  // === OBSERVABLES FROM SIGNALS ===
  private subRouteId$ = toObservable(this.subRouteId);
  private groupingCriteria$ = toObservable(this.groupingCriteria);
  private searchTerm$ = toObservable(this.searchTerm);

  constructor() {
    // Search term resets to page 1 and clears data
    this.searchTerm$
      .pipe(debounceTime(400), distinctUntilChanged(), skip(1), takeUntilDestroyed())
      .subscribe(() => {
        this._currentPage.set(1);
        this._data.set([]);
        this._hasMore.set(true);
        this.triggerLoad();
      });

    // Grouping criteria change resets to page 1 and clears data
    this.groupingCriteria$.pipe(skip(1), takeUntilDestroyed()).subscribe(() => {
      this._currentPage.set(1);
      this._data.set([]);
      this._hasMore.set(true);
      this.triggerLoad();
    });

    // Initial load when subRouteId and groupingCriteria are available
    combineLatest([
      this.subRouteId$.pipe(filter((id): id is string => !!id)),
      this.groupingCriteria$.pipe(filter((c) => c !== BPGroupingCriteria.NONE)),
    ])
      .pipe(debounceTime(50), takeUntilDestroyed())
      .subscribe(() => {
        this._currentPage.set(1);
        this._data.set([]);
        this._hasMore.set(true);
        this.triggerLoad();
      });

    // Load more handler
    this.loadMore$
      .pipe(
        filter(() => this._hasMore() && !this._loadingMore() && !this._loading()),
        switchMap(() => {
          this._loadingMore.set(true);
          const nextPage = this._currentPage() + 1;
          return this.fetchGroupedData(nextPage);
        }),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (response) => {
          const newItems = this.mapApiResponseToItems(response?.data);
          this._data.update((current) => [...current, ...newItems]);
          this._currentPage.update((p) => p + 1);
          this._hasMore.set(newItems.length === this.pageSize);
          this._loadingMore.set(false);
        },
        error: (err) => {
          this._loadingMore.set(false);
          this.showLoadError(err);
        },
      });

    // Manual reload
    this.reload$.pipe(takeUntilDestroyed()).subscribe(() => {
      this._currentPage.set(1);
      this._data.set([]);
      this._hasMore.set(true);
      this.triggerLoad();
    });
  }

  private triggerLoad(): void {
    const subRouteId = this.subRouteId();
    const groupBy = this.groupingCriteria();

    if (!subRouteId || groupBy === BPGroupingCriteria.NONE) return;

    this._loading.set(true);

    this.fetchGroupedData(1).subscribe({
      next: (response) => {
        const items = this.mapApiResponseToItems(response?.data);
        this._data.set(items);
        this._totalItems.set(response?.data?.pageInfo?.[0]?.total ?? items.length);
        this._hasMore.set(items.length === this.pageSize);
        this._loading.set(false);
      },
      error: (err) => {
        this._loading.set(false);
        this._data.set([]);
        this.showLoadError(err);
      },
    });
  }

  private showLoadError(err: { error?: { message?: string } }): void {
    this.handlerMessage.error({
      message: err?.error?.message,
      alternativeMessage: t('routing.SUB_ROUTE_PARTNERS.ERRORS.LOAD_FAILED'),
    });
  }

  private fetchGroupedData(page: number) {
    const subRouteId = this.subRouteId();
    const groupBy = this.groupingCriteria();

    return this.businessPartnerService.countUnassignedByBpFrequency(
      subRouteId!,
      groupBy,
      {
        currentPage: page,
        limitPerPage: this.pageSize,
        search: this.searchTerm(),
        sort: 'name',
        sortDirection: 'ASC',
      },
      this.panelService.subRouteData()?.company?._id?.toString() ?? null
    );
  }

  private mapApiResponseToItems(groupedData: BPGroupedResponse | undefined): BPGroupedItem[] {
    const dataArray: BPGroupedApiItem[] = groupedData?.data ?? [];

    if (dataArray.length === 0) return [];

    return dataArray.map((item, index) => {
      const trackingId =
        item._id || (item.id != null ? String(item.id) : `no-group-${item.name || index}`);
      return {
        _id: trackingId,
        id: item.id ?? null,
        name: item.name || t('routing.SUB_ROUTE_PARTNERS.GROUPED.NO_NAME_FALLBACK'),
        // i18n-exempt: 'S/N' is also a comparison sentinel (see buildFiltersForGroup) — not display copy
        code: item.code || 'S/N',
        totalBusinessPartners: item.totalBusinessPartners || item.count || 0,
        businessPartners: item.businessPartners || [],
      };
    });
  }
}
